import type { ReactNode } from 'react';
import { CITATION_REGEX } from '@/features/chat/lib/parseCitations';

/**
 * Minimal allowlist parser for the doctor summary. Only two markups are honored:
 *   - `**bold**` — rendered as <strong>
 *   - `[ref:UUID]` — rendered as a tappable superscript chip that jumps to the
 *     Timeline tab and focuses the cited event.
 *
 * Anything else is plain text. No full markdown (prompt-engineer blocker — a full
 * parser opens injection vectors).
 */

export type SummaryNode =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'citation'; eventId: string; index: number };

export type SummaryParagraph = SummaryNode[];

// Matches either a bold span (**...**) OR a [ref:UUID] citation. Non-greedy bold
// so that two adjacent bold spans don't merge.
const TOKEN_REGEX =
  /\*\*([^*\n]+?)\*\*|\[ref:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/g;

export function parseSummary(text: string): SummaryParagraph[] {
  // Pass 1 — collect UUID order so citation indices are consistent across
  // paragraphs (the same event always gets the same [N]).
  const order: string[] = [];
  const citationRegex = new RegExp(CITATION_REGEX.source, 'g');
  for (const m of text.matchAll(citationRegex)) {
    const uuid = m[1];
    if (!order.includes(uuid)) order.push(uuid);
  }

  const paragraphs = text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return paragraphs.map((p) => tokenizeParagraph(p, order));
}

function tokenizeParagraph(paragraph: string, uuidOrder: string[]): SummaryParagraph {
  const nodes: SummaryParagraph = [];
  const re = new RegExp(TOKEN_REGEX.source, 'g');
  let cursor = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(paragraph)) !== null) {
    const start = m.index;
    if (start > cursor) {
      nodes.push({ kind: 'text', text: paragraph.slice(cursor, start) });
    }
    if (m[1] !== undefined) {
      nodes.push({ kind: 'bold', text: m[1] });
    } else if (m[2] !== undefined) {
      const uuid = m[2];
      const idx = uuidOrder.indexOf(uuid) + 1;
      nodes.push({ kind: 'citation', eventId: uuid, index: idx });
    }
    cursor = start + m[0].length;
  }
  if (cursor < paragraph.length) {
    nodes.push({ kind: 'text', text: paragraph.slice(cursor) });
  }
  return nodes;
}

export function renderSummaryNodes(
  paragraph: SummaryParagraph,
  onCitationClick: (eventId: string) => void,
): ReactNode[] {
  return paragraph.map((node, i) => {
    if (node.kind === 'text') {
      return <span key={i}>{node.text}</span>;
    }
    if (node.kind === 'bold') {
      return (
        <strong key={i} className="font-bold text-ink">
          {node.text}
        </strong>
      );
    }
    return (
      <button
        key={i}
        type="button"
        onClick={() => onCitationClick(node.eventId)}
        className="inline-flex items-center justify-center mx-0.5 px-1.5 min-w-[22px] h-5 rounded-full text-xs font-semibold align-baseline tabular-nums text-evt-rx-ink bg-evt-rx-soft border border-evt-rx/30 hover:bg-evt-rx hover:text-white print:bg-white print:border-ink/30 print:text-black"
        aria-label={`See record ${node.index} in the timeline`}
      >
        {node.index}
      </button>
    );
  });
}
